import React, { useRef, useEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const HistorySection = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const timelineRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (prefersReducedMotion) return;

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: section,
        start: "top 60%",
        toggleActions: "play none none none",
      },
    });

    tl.from(section.querySelector("h2"), {
      opacity: 0,
      y: 50,
      duration: 1,
      ease: "power3.out",
    }).from(timelineRef.current, { opacity: 0, duration: 0.5 }, "-=0.5");

    const items = gsap.utils.toArray(".timeline-item");
    items.forEach((item) => {
      gsap.from(item as HTMLElement, {
        opacity: 0,
        y: 50,
        duration: 0.8,
        ease: "power3.out",
        scrollTrigger: {
          trigger: item as HTMLElement,
          start: "top 85%",
          toggleActions: "play none none none",
        },
      });
    });

    const founderCards = gsap.utils.toArray(".founder-card");
    gsap.from(founderCards, {
      opacity: 0,
      scale: 0.9,
      stagger: 0.2,
      duration: 0.6,
      ease: "power2.out",
      scrollTrigger: {
        trigger: ".founders-container",
        start: "top 80%",
        toggleActions: "play none none none",
      },
    });

    return () => {
      ScrollTrigger.getAll().forEach((t) => t.kill());
    };
  }, []);

  const founders = [
    {
      name: "Chief Okoro",
      title: "The Visionary",
      image:
        "https://source.unsplash.com/random/100x100?portrait,elder,african-man",
    },
    {
      name: "Olori Adanna",
      title: "The Builder",
      image:
        "https://source.unsplash.com/random/100x100?portrait,elder,african-woman",
    },
    {
      name: "Eze Nwoke",
      title: "The Unifier",
      image:
        "https://source.unsplash.com/random/100x100?portrait,african-leader",
    },
  ];

  return (
    <section ref={sectionRef} className="bg-stone-50 py-20 md:py-28">
      <div className="mx-auto max-w-6xl px-6">
        <h2 className="mb-20 text-center font-serif text-4xl font-semibold tracking-wide text-nature-green-dark md:text-5xl">
          The Story of Our Village
        </h2>

        <div className="relative">
          {/* Timeline Line */}
          <div
            ref={timelineRef}
            className="absolute left-4 top-0 h-full w-0.5 bg-amber-200/80 md:left-1/2 md:-translate-x-1/2 md:transform"
          ></div>

          {/* Timeline Item: Origins */}
          <div className="timeline-item mb-20 flex w-full flex-col items-center md:flex-row md:justify-between">
            <div className="order-1 md:w-5/12">
              <div className="rounded-xl border border-stone-200/60 bg-white p-6 shadow-sm">
                <p className="font-semibold text-amber-600">Ancient Times</p>
                <h3 className="mt-1 text-2xl font-semibold text-gray-800">
                  Our Origins
                </h3>
                <p className="mt-4 text-lg leading-relaxed text-gray-700">
                  Born from the fertile lands nestled between the great river
                  and the whispering hills, our village began as a small
                  settlement of farmers and hunters, drawn together by a shared
                  spirit of resilience.
                </p>
              </div>
            </div>
            <div className="z-10 my-4 flex h-16 w-16 items-center justify-center rounded-full bg-nature-green-dark shadow-lg md:my-0">
              <span className="text-2xl font-bold text-white">1</span>
            </div>
            <div className="order-2 md:w-5/12"></div>
          </div>

          {/* Timeline Item: Migration */}
          <div className="timeline-item mb-20 flex w-full flex-col items-center md:flex-row md:justify-between">
            <div className="order-2 md:order-none md:w-5/12"></div>
            <div className="z-10 my-4 flex h-16 w-16 items-center justify-center rounded-full bg-nature-green-dark shadow-lg md:my-0">
              <span className="text-2xl font-bold text-white">2</span>
            </div>
            <div className="order-1 md:order-2 md:w-5/12">
              <div className="rounded-xl border border-stone-200/60 bg-white p-6 shadow-sm">
                <p className="font-semibold text-amber-600">
                  The Great Journey
                </p>
                <h3 className="mt-1 text-2xl font-semibold text-gray-800">
                  The Migration Story
                </h3>
                <p className="mt-4 text-lg leading-relaxed text-gray-700">
                  Guided by the stars and the wisdom of our ancestors, our
                  people embarked on a great journey, seeking a land of peace
                  and prosperity. This very ground became the fulfillment of
                  that sacred quest.
                </p>
              </div>
            </div>
          </div>

          {/* Timeline Item: Founders */}
          <div className="timeline-item flex w-full flex-col items-center md:flex-row md:justify-between">
            <div className="order-1 md:w-5/12">
              <div className="rounded-xl border border-stone-200/60 bg-white p-6 shadow-sm">
                <p className="font-semibold text-amber-600">The Pillars</p>
                <h3 className="mt-1 text-2xl font-semibold text-gray-800">
                  Our Esteemed Founders
                </h3>
                <div className="founders-container mt-6 space-y-4">
                  {founders.map((founder, index) => (
                    <div
                      key={index}
                      className="founder-card flex items-center gap-4 rounded-xl border border-stone-200/80 bg-stone-50 p-3"
                    >
                      <img
                        src={founder.image}
                        alt={founder.name}
                        className="h-16 w-16 rounded-full object-cover"
                      />
                      <div>
                        <h4 className="font-bold text-gray-800">
                          {founder.name}
                        </h4>
                        <p className="text-sm text-gray-600">{founder.title}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="z-10 my-4 flex h-16 w-16 items-center justify-center rounded-full bg-nature-green-dark shadow-lg md:my-0">
              <span className="text-2xl font-bold text-white">3</span>
            </div>
            <div className="order-2 md:w-5/12"></div>
          </div>
        </div>
        <div className="from-transparent to-transparent mt-28 h-px w-full bg-gradient-to-r via-amber-300"></div>
      </div>
    </section>
  );
};

export default HistorySection;
